import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import apiClient from "../utils/axiosConfig";
import { getAccessToken } from "../utils/authToken";
import { isPublicAppPath } from "../utils/publicPaths";
import { applyRuntimeDormantFeatures } from "../utils/dormantFeatures.js";
import { applyRuntimeOptionsTrust } from "../utils/optionsTrustFlag.js";
import { applyRuntimeSurveyOneTapQ1 } from "../utils/surveyOneTapFlag";
import { applyRuntimeReengage } from "../utils/reengageFlag";

const AppContext = createContext({
  mapsApiKey: "",
  weather: null,
  locationLabel: "",
  profile: null,
  notificationCount: 0,
  avatarUrl: "",
  userInitials: "D",
  loading: true,
  refresh: () => {},
});

function buildInitials(profile) {
  const first = profile?.first_name?.[0] || "";
  const last = profile?.last_name?.[0] || "";
  const combined = `${first}${last}`.toUpperCase();
  return combined || "D";
}

function buildAvatarUrl(profile) {
  if (profile?.avatar_url) return profile.avatar_url;
  const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim();
  if (name) {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=7c3aed&color=fff&size=128`;
  }
  return "";
}

export function AppContextProvider({ children }) {
  const [state, setState] = useState({
    mapsApiKey: "",
    weather: null,
    locationLabel: "",
    profile: null,
    notificationCount: 0,
    loading: true,
  });

  const refresh = useCallback(async () => {
    // A signed-out visitor on a public page has no account context. Calling the
    // account endpoints would 401, and the API client would then send them to login.
    if (!getAccessToken() && isPublicAppPath(window.location.pathname)) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }
    try {
      const [ctxRes, profileRes] = await Promise.allSettled([
        apiClient.get("/api/app-context"),
        apiClient.get("/api/profile/"),
      ]);

      const ctx =
        ctxRes.status === "fulfilled"
          ? ctxRes.value?.data || {}
          : ctxRes.reason?.response?.data || {};
      if (ctx.features) {
        applyRuntimeDormantFeatures(ctx.features);
        applyRuntimeOptionsTrust(ctx.features);
        applyRuntimeSurveyOneTapQ1(ctx.features);
        applyRuntimeReengage(ctx.features);
      }
      const profile =
        profileRes.status === "fulfilled"
          ? { ...ctx.profile, ...profileRes.value?.data }
          : ctx.profile || {};

      setState({
        mapsApiKey: String(ctx.google_maps_api_key || "").trim(),
        weather: ctx.weather || null,
        locationLabel: String(ctx.location_label || "").trim(),
        profile,
        notificationCount: Number(ctx.notification_count || 0),
        loading: false,
      });
    } catch {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      ...state,
      avatarUrl: buildAvatarUrl(state.profile),
      userInitials: buildInitials(state.profile),
      refresh,
    }),
    [state, refresh]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  return useContext(AppContext);
}
